import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Card, CardBody, Row, Col, FormGroup, Label, Input } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import LoadingButton from "../../components/Common/LoadingButton"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import { loggedUser as refreshUser } from "../../store/auth/login/actions"
import { getAppSettings, saveAppSettings } from "../../services/developer"
import "../Tenancy/tenancy.css"

const DATE_FORMATS = {
  "d-m-Y": "31-12-2026",
  "d/m/Y": "31/12/2026",
  "Y-m-d": "2026-12-31",
  "m/d/Y": "12/31/2026",
  "d M Y": "31 Dec 2026",
  "d-M-Y": "31-Dec-2026",
}

const Field = ({ label, hint, children }) => (
  <FormGroup className="mb-0">
    <Label className="dm-label">{label}</Label>
    {children}
    {hint && <small className="text-muted d-block mt-1" style={{ fontSize: 11 }}>{hint}</small>}
  </FormGroup>
)

/** Calyvern "Assembly" cube, recoloured per theme (matches the brand palette). */
const THEMES = {
  blue: { label: "Blue", top: "#2F57C4", left: "#0C36A5", right: "#08267A", tile: "#0C36A5" },
  grey: { label: "Grey", top: "#9CA3AD", left: "#565C66", right: "#23272E", tile: "#1B1E25" },
}

const Cube = ({ theme, size = 40 }) => {
  const t = THEMES[theme] || THEMES.blue
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <polygon points="32,12 50,22 32,32 14,22" fill={t.top} />
      <polygon points="14,22 32,32 32,50 14,40" fill={t.left} />
      <polygon points="50,22 32,32 32,50 50,40" fill={t.right} />
      <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
        <line x1="23" y1="17" x2="41" y2="27" /><line x1="41" y1="17" x2="23" y2="27" />
        <line x1="23" y1="27" x2="23" y2="45" /><line x1="14" y1="31" x2="32" y2="41" />
        <line x1="41" y1="27" x2="41" y2="45" /><line x1="50" y1="31" x2="32" y2="41" />
      </g>
    </svg>
  )
}

/**
 * Developer > Application Settings — global branding/format for the whole app, mirroring
 * StaffFlow's three-card layout (Identity · Currency · Formatting). Backed by the legacy
 * global `settings` row via /developer/application-settings. Gated by the `developer` mw.
 */
const ApplicationSettings = () => {
  document.title = "Application Settings | Developer"
  const dispatch = useDispatch()
  const imgUrl = process.env.REACT_APP_IMG_APIKEY
  const [saving, setSaving] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const [form, setForm] = useState({
    app_name: "", app_subtitle: "", theme: "blue", currency_code: "", currency_symbol: "",
    date_format: "d-m-Y", desimal_point: "2", logo: null, favicon: null, camp_floor_bed: false,
    depreciation_pct: "15",
  })
  const [logoUrl, setLogoUrl] = useState(null)
  const [faviconUrl, setFaviconUrl] = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    ;(async () => {
      try {
        const d = (await getAppSettings())?.data || {}
        setForm({
          app_name: d.companyname || "",
          app_subtitle: d.tagline || "",
          theme: d.theme === "grey" ? "grey" : "blue",
          currency_code: d.currencycode || "",
          currency_symbol: d.currencysymbol || "",
          date_format: d.dateformat || "d-m-Y",
          desimal_point: d.desimal_point ?? "2",
          logo: null, favicon: null,
          camp_floor_bed: !!d.camp_floor_bed,
          depreciation_pct: d.depreciation_pct ?? "15",
        })
        setLogoUrl(d.companylogo ? `${imgUrl}${d.companylogo}` : null)
        setFaviconUrl(d.companyfavicon ? `${imgUrl}${d.companyfavicon}` : null)
      } catch (e) {
        toastError(e?.message || "Failed to load application settings")
      }
    })()
  }, [refresh]) // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (k, file) => {
    if (!file) return
    set(k, file)
    const url = URL.createObjectURL(file)
    k === "logo" ? setLogoUrl(url) : setFaviconUrl(url)
  }

  const submit = async e => {
    e.preventDefault()
    try {
      setSaving(true)
      const fd = new FormData()
      fd.append("companyname", form.app_name || "")
      fd.append("tagline", form.app_subtitle || "")
      fd.append("theme", form.theme || "blue")
      fd.append("currencycode", form.currency_code || "")
      fd.append("currencysymbol", form.currency_symbol || "")
      fd.append("dateformat", form.date_format || "")
      fd.append("desimal_point", form.desimal_point === "" ? "" : form.desimal_point)
      fd.append("camp_floor_bed", form.camp_floor_bed ? "1" : "0")
      fd.append("depreciation_pct", form.depreciation_pct === "" ? "" : form.depreciation_pct)
      if (form.logo) fd.append("companylogo", form.logo)
      if (form.favicon) fd.append("companyfavicon", form.favicon)
      await saveAppSettings(fd)
      // Keep the in-app theme switcher in sync (blue=2 / grey=3) so both agree.
      localStorage.setItem("themeManage", form.theme === "grey" ? "3" : "2")
      toastSuccess("Application settings saved")
      setRefresh(r => r + 1)
      // Refresh the logged-in user so the new theme/logo/favicon apply live
      // (App.js re-applies the palette + favicon; sidebar/header logos re-render).
      dispatch(refreshUser())
    } catch (err) {
      toastError(err?.message || "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  const sampleAmount = `${form.currency_symbol || "—"} ${(1000).toFixed(Math.min(6, Math.max(0, Number(form.desimal_point) || 0)))}`

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <Breadcrumbs
          title="Developer"
          breadcrumbObject={[
            { title: "Developer", link: "/developer" },
            { title: "Application Settings", link: "#" },
          ]}
        />

        <form onSubmit={submit} style={{ maxWidth: 860 }}>
          {/* ── Identity ──────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Identity</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>Branding shown across the application — name, logo and browser favicon.</p>
              <Row className="g-3">
                <Col md={6}>
                  <Field label="Application name *">
                    <Input value={form.app_name} onChange={e => set("app_name", e.target.value)} placeholder="e.g. Calyvern" />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Subtitle" hint="shown under the name, e.g. Business OS · Govern">
                    <Input value={form.app_subtitle} onChange={e => set("app_subtitle", e.target.value)} placeholder="Business OS · Govern" />
                  </Field>
                </Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Field label="Logo" hint="PNG / SVG — shown in the header">
                    {logoUrl && <img src={logoUrl} alt="logo" className="mb-2 d-block" style={{ height: 40, maxWidth: 160, objectFit: "contain", background: "#fff", border: "1px solid #e7e9ef", borderRadius: 6, padding: 4 }} />}
                    <Input type="file" accept="image/*" bsSize="sm" onChange={e => onFile("logo", e.target.files?.[0])} />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Favicon" hint="PNG / ICO — browser tab icon">
                    {faviconUrl && <img src={faviconUrl} alt="favicon" className="mb-2 d-block" style={{ height: 32, width: 32, objectFit: "contain", background: "#fff", border: "1px solid #e7e9ef", borderRadius: 6, padding: 3 }} />}
                    <Input type="file" accept="image/png,image/x-icon,image/jpeg,image/svg+xml" bsSize="sm" onChange={e => onFile("favicon", e.target.files?.[0])} />
                  </Field>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ── Currency ──────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Currency</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>Used throughout the application — on money fields, rate labels and reports.</p>
              <Row className="g-3">
                <Col md={6}>
                  <Field label="Currency code *" hint="e.g. AED, USD, INR">
                    <Input value={form.currency_code} onChange={e => set("currency_code", e.target.value.toUpperCase())} />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Currency symbol *" hint="shown on labels, e.g. AED, $, ₹">
                    <Input value={form.currency_symbol} onChange={e => set("currency_symbol", e.target.value)} />
                  </Field>
                </Col>
              </Row>
              <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
                Preview: amount shows <strong>{sampleAmount}</strong>
              </p>
            </CardBody>
          </Card>

          {/* ── Theme ─────────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Theme</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>Brand colour + logo/favicon variant used across the application.</p>
              <Row className="g-3">
                {Object.entries(THEMES).map(([key, th]) => (
                  <Col md={6} key={key}>
                    <div
                      role="button"
                      onClick={() => set("theme", key)}
                      style={{
                        border: form.theme === key ? "2px solid #2563eb" : "1px solid #e7e9ef",
                        borderRadius: 10, padding: 14, cursor: "pointer", background: "#fff",
                      }}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ background: th.tile, borderRadius: 8, padding: 6, display: "inline-flex" }}>
                          <Cube theme={key} size={34} />
                        </div>
                        <div className="ms-3">
                          <div style={{ fontWeight: 600 }}>{th.label}</div>
                          {form.theme === key
                            ? <div className="text-primary" style={{ fontSize: 12 }}>Selected</div>
                            : <div className="text-muted" style={{ fontSize: 12 }}>Click to select</div>}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap small text-muted" style={{ gap: 12 }}>
                        {[["Top", th.top], ["Left", th.left], ["Right", th.right]].map(([n, c]) => (
                          <span key={n}><span style={{ display: "inline-block", width: 10, height: 10, background: c, borderRadius: 2, marginRight: 4, verticalAlign: "middle" }} />{n} · {c}</span>
                        ))}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {/* Live lockup preview */}
              <div className="mt-3 d-flex align-items-center" style={{ border: "1px solid #eef0f4", borderRadius: 10, padding: 16 }}>
                <div style={{ background: (THEMES[form.theme] || THEMES.blue).tile, borderRadius: 10, padding: 8, display: "inline-flex" }}>
                  <Cube theme={form.theme} size={40} />
                </div>
                <div className="ms-3">
                  <div style={{ fontFamily: "Sora, system-ui, sans-serif", fontWeight: 600, fontSize: 24, color: "#14171c", lineHeight: 1.1 }}>
                    {form.app_name || "Calyvern"}
                  </div>
                  {form.app_subtitle && <div className="text-muted" style={{ fontSize: 13 }}>{form.app_subtitle}</div>}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ── Formatting ────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Formatting</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>The common date format and number precision used across the application.</p>
              <Row className="g-3">
                <Col md={6}>
                  <Field label="Date format *">
                    <Input type="select" value={form.date_format} onChange={e => set("date_format", e.target.value)}>
                      {Object.entries(DATE_FORMATS).map(([value, ex]) => <option key={value} value={value}>{value}  ({ex})</option>)}
                    </Input>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Decimal places *" hint="digits after the decimal point (0–6)">
                    <Input type="number" min="0" max="6" value={form.desimal_point} onChange={e => set("desimal_point", e.target.value)} />
                  </Field>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ── Modules ───────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Camp Management</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>Optional capabilities for the Camp Management module.</p>
              <div className="form-check form-switch">
                <Input
                  type="checkbox"
                  role="switch"
                  id="camp_floor_bed"
                  checked={!!form.camp_floor_bed}
                  onChange={e => set("camp_floor_bed", e.target.checked)}
                />
                <label htmlFor="camp_floor_bed" className="form-check-label ms-2">
                  Enable floor &amp; bed-level management
                </label>
              </div>
              <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
                When on, the Camp Manager tracks each camp as Floor → Room → Bed, with per-bed
                occupied / vacant / on-vacation status. When off, rooms use simple capacity /
                occupied counts.
              </p>
            </CardBody>
          </Card>

          {/* ── Fleet ─────────────────────────────────────────────────── */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-1">Fleet</h6>
              <p className="text-muted mb-3" style={{ fontSize: 13 }}>Depreciation applied to vehicles in the fleet Transportation Analysis report.</p>
              <Row className="g-3">
                <Col md={6}>
                  <Field label="Depreciation % per year" hint="e.g. 15 — annual rate on the vehicle's asset value">
                    <Input type="number" min="0" max="100" step="0.01" value={form.depreciation_pct}
                      onChange={e => set("depreciation_pct", e.target.value)} />
                  </Field>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <div className="d-flex justify-content-end mb-4" style={{ maxWidth: 860 }}>
            <LoadingButton type="submit" color="primary" loading={saving} disabled={saving} className="btn btn-primary">
              Save settings
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ApplicationSettings
